import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Save, Map as MapIcon, AlertTriangle, Loader2, Pencil, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';

interface OrderStatusMaster { id: number; code: string; name: string; category?: string }
interface NdrMaster { id: number; name: string }
interface Courier { id: number; name: string; brand?: string; orderType?: string }
type Tab = 'status' | 'ndr';
type Row = { id: number; label: string; codes: string };

export function CourierMappingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.kind === 'user' && user.userType === 'SUPER_ADMIN';
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const [tab, setTab] = useState<Tab>('status');
  const [showCreate, setShowCreate] = useState(false);
  const [viewCourierId, setViewCourierId] = useState<number | ''>('');

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MapIcon size={22} className="text-emerald-700" /> Status & NDR Mapping
        </h1>
        <button onClick={() => setShowCreate(true)} className="px-3 py-2 bg-emerald-600 text-white rounded text-sm flex items-center gap-1">
          <Plus size={14} /> Create Mapping
        </button>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {(['status','ndr'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-1.5 text-sm rounded-md transition ${tab === t ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t === 'status' ? 'Courier Status' : 'NDR'}
          </button>
        ))}
      </div>

      <ViewMapping tab={tab} courierId={viewCourierId} onCourierChange={setViewCourierId} />

      {showCreate && <CreateMappingDialog tab={tab} onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function ViewMapping({ tab, courierId, onCourierChange }: { tab: Tab; courierId: number | ''; onCourierChange: (v: number | '') => void }) {
  const qc = useQueryClient();
  const { data: couriers } = useQuery({ queryKey: ['couriers','active'], queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data });
  const { data: statuses } = useQuery({ queryKey: ['order-statuses'], queryFn: async () => (await api.get<OrderStatusMaster[]>('/order-statuses')).data, enabled: tab === 'status' });
  const { data: ndrMaster } = useQuery({ queryKey: ['ndr-master'], queryFn: async () => (await api.get<{ items?: NdrMaster[] }>('/settings/ndr_statuses')).data?.items ?? [], enabled: tab === 'ndr' });

  const endpoint = tab === 'status' ? '/courier-mappings/status' : '/courier-mappings/ndr';
  const { data: mapping = [], isLoading } = useQuery({
    queryKey: ['courier-mapping', tab, courierId],
    enabled: !!courierId,
    queryFn: async () => (await api.get<{ orderStatusId?: number; ndrCode?: number; codes: string[] }[]>(endpoint, { params: { courierId } })).data,
  });

  const master: { id: number; name: string }[] = tab === 'status'
    ? (statuses ?? []).map((s) => ({ id: s.id, name: `${s.id} · ${s.name}` }))
    : (ndrMaster ?? []).map((n) => ({ id: n.id, name: `${n.id} · ${n.name}` }));

  const codesFor = (id: number): string[] => {
    const r = mapping.find((m: any) => (tab === 'status' ? m.orderStatusId : m.ndrCode) === id);
    return r ? r.codes : [];
  };

  // Local edit state per (tab, id) — we only edit one row at a time
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  // Reset edit state when tab or courier changes
  useEffect(() => { setEditingId(null); setEditValue(''); }, [tab, courierId]);

  const saveRow = useMutation({
    mutationFn: async ({ id, codes }: { id: number; codes: string[] }) => {
      const payload = { courierIds: [Number(courierId)], rows: [{ id, codes }] };
      return (await api.post(endpoint, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courier-mapping', tab, courierId] });
      toast.success('Mapping updated');
      setEditingId(null);
      setEditValue('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const deleteRow = useMutation({
    mutationFn: async (id: number) => {
      const delEp = tab === 'status'
        ? `/courier-mappings/status/${Number(courierId)}/${id}`
        : `/courier-mappings/ndr/${Number(courierId)}/${id}`;
      return (await api.delete(delEp)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courier-mapping', tab, courierId] });
      toast.success('Mapping cleared');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Delete failed'),
  });

  const startEdit = (id: number) => {
    setEditingId(id);
    setEditValue(codesFor(id).join(', '));
  };
  const cancelEdit = () => { setEditingId(null); setEditValue(''); };
  const commitEdit = (id: number) => {
    const codes = editValue.split(',').map((s) => s.trim()).filter(Boolean);
    saveRow.mutate({ id, codes });
  };
  const askDelete = (id: number, label: string) => {
    if (!confirm(`Clear mapped codes for "${label}"?`)) return;
    deleteRow.mutate(id);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-sm font-medium">View / edit existing mapping for courier:</span>
        <div className="w-72">
          <SearchableSelect value={courierId === '' ? '' : courierId}
            onChange={(v) => onCourierChange(v === '' ? '' : Number(v))}
            options={(couriers ?? []).map((c) => ({ value: c.id, label: c.name + ' (#' + c.id + ')', hint: c.brand ?? '' }))}
            placeholder="Select courier to view" />
        </div>
      </div>
      {!courierId && <div className="text-sm text-slate-500 py-6 text-center border-2 border-dashed rounded">Pick a courier above to see its current mapping</div>}
      {courierId && isLoading && <div className="text-sm text-slate-500 py-6 text-center"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading…</div>}
      {courierId && !isLoading && (
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="px-3 py-2 text-left w-1/3">Internal {tab === 'status' ? 'Status' : 'NDR Reason'}</th>
            <th className="px-3 py-2 text-left">Mapped Courier Codes</th>
            <th className="px-3 py-2 text-right w-32">Actions</th>
          </tr></thead>
          <tbody>
            {master.map((m) => {
              const codes = codesFor(m.id);
              const isEditing = editingId === m.id;
              const hasCodes = codes.length > 0;
              return (
                <tr key={m.id} className="border-t">
                  <td className="px-3 py-1.5 text-slate-700 align-top whitespace-nowrap">{m.name}</td>
                  <td className="px-3 py-1.5 font-mono text-xs align-top">
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEdit(m.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        placeholder='e.g. "Manifested, MANIFESTED, 1"'
                        className="w-full border border-emerald-400 rounded px-2 py-1 text-xs font-mono"
                      />
                    ) : (
                      hasCodes ? codes.join(', ') : <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1 text-right align-top">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => commitEdit(m.id)}
                          disabled={saveRow.isPending}
                          className="px-2 py-1 text-xs bg-emerald-600 text-white rounded inline-flex items-center gap-1 disabled:opacity-50"
                          title="Save"
                        >
                          {saveRow.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                        </button>
                        <button onClick={cancelEdit} className="px-2 py-1 text-xs border rounded inline-flex items-center gap-1" title="Cancel">
                          <X size={12} /> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(m.id)}
                          className="px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 rounded inline-flex items-center gap-1"
                          title="Edit"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        {hasCodes && (
                          <button
                            onClick={() => askDelete(m.id, m.name)}
                            disabled={deleteRow.isPending}
                            className="px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 rounded inline-flex items-center gap-1 disabled:opacity-50"
                            title="Clear codes"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CreateMappingDialog({ tab, onClose }: { tab: Tab; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: couriers } = useQuery({ queryKey: ['couriers','active'], queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data });
  const { data: statuses } = useQuery({ queryKey: ['order-statuses'], queryFn: async () => (await api.get<OrderStatusMaster[]>('/order-statuses')).data, enabled: tab === 'status' });
  const { data: ndrMaster } = useQuery({ queryKey: ['ndr-master'], queryFn: async () => (await api.get<{ items?: NdrMaster[] }>('/settings/ndr_statuses')).data?.items ?? [], enabled: tab === 'ndr' });

  const [courierIds, setCourierIds] = useState<(string | number)[]>([]);
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (tab === 'status' && statuses) {
      setRows(statuses.map((s) => ({ id: s.id, label: `${s.id} · ${s.name}`, codes: '' })));
    } else if (tab === 'ndr' && ndrMaster) {
      setRows(ndrMaster.map((n) => ({ id: n.id, label: `${n.id} · ${n.name}`, codes: '' })));
    }
  }, [tab, statuses, ndrMaster]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        courierIds: courierIds.map(Number),
        rows: rows
          .filter((r) => r.codes.trim().length > 0)
          .map((r) => ({ id: r.id, codes: r.codes.split(',').map((s) => s.trim()).filter(Boolean) })),
      };
      const endpoint = tab === 'status' ? '/courier-mappings/status' : '/courier-mappings/ndr';
      return (await api.post(endpoint, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courier-mapping'] });
      toast.success(`Mapping saved for ${courierIds.length} courier(s)`);
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const filled = rows.filter((r) => r.codes.trim().length > 0).length;

  return (
    <div className="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Create Mapping — {tab === 'status' ? 'Courier Status' : 'NDR'}</h2>
          <button onClick={onClose}><X size={18} className="text-slate-400 hover:text-slate-700" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div className="mb-4">
            <label className="text-sm font-medium block mb-1">1. Pick couriers <span className="text-rose-600">*</span></label>
            <div className="text-xs text-slate-500 mb-2">The same matrix below will be applied to every courier you select. You can run this multiple times if codes differ per courier.</div>
            <MultiSelectFilter label="" placeholder="Select couriers" values={courierIds}
              onChange={setCourierIds}
              options={(couriers ?? []).map((c) => ({ value: c.id, label: c.name + ' (#' + c.id + ')', hint: c.brand ?? '' }))} />
          </div>

          <div className="mb-2">
            <label className="text-sm font-medium block mb-1">2. Map our {tab === 'status' ? 'order statuses' : 'NDR reasons'} → courier raw codes</label>
            <div className="text-xs text-slate-500 mb-2">Enter comma-separated codes the courier sends for each of our internal {tab === 'status' ? 'statuses' : 'NDR reasons'}. Leave blank to skip.</div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-emerald-50">
                <tr>
                  <th className="px-3 py-2 text-left w-2/5">Internal {tab === 'status' ? 'Status' : 'NDR Reason'}</th>
                  <th className="px-3 py-2 text-left">Courier raw codes (comma-separated)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-1.5 text-slate-700 align-top whitespace-nowrap">{r.label}</td>
                    <td className="px-2 py-1">
                      <input value={r.codes}
                        onChange={(e) => setRows((arr) => arr.map((x, j) => j === i ? { ...x, codes: e.target.value } : x))}
                        placeholder='e.g. "Manifested, MANIFESTED, 1"'
                        className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between bg-slate-50">
          <div className="text-xs text-slate-500">
            {courierIds.length === 0 && <span className="text-amber-700"><AlertTriangle size={12} className="inline mr-1" />Pick at least one courier.</span>}
            {courierIds.length > 0 && <>Will save <b>{filled}</b> non-empty row(s) × <b>{courierIds.length}</b> courier(s)</>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
            <button onClick={() => save.mutate()} disabled={courierIds.length === 0 || filled === 0 || save.isPending}
              className="px-4 py-2 bg-emerald-600 text-white rounded text-sm flex items-center gap-1 disabled:opacity-50">
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Mapping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
